import { useEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import * as adminApi from '../admin/adminApi.ts';
import type { AdminUser } from '../admin/adminApi.ts';
import {
  SETUP_LINK_HOURS,
  USER_STATE_KEY,
  afterCreate,
  parseQuotaInput,
  patchFor,
  quotaCell,
  roleAction,
  setupUrl,
  stateAction,
  userState,
  userStateNote,
} from '../admin/derive.ts';
import type { PatchAction } from '../admin/derive.ts';
import type { Me, Role } from '../auth/authApi.ts';
import { AdminPage, LoadState, errorText, useLoaded } from '../components/AdminPage.tsx';
import { Field } from '../components/Field.tsx';

/** A setup link, held once. It lives in this component's state and nowhere else. */
export interface Secret {
  email: string;
  url: string;
  /** What made it, so the sentence can say so. */
  reason: 'created' | 'reset';
}

/** The one row that is in an inline edit or a confirm step. Only one is open at a time. */
export type StepKind = 'quota' | 'reset' | PatchAction;
export interface Step {
  /** For a PATCH action this is the absolute intent of the button that was pressed (derive.ts,
   * `PatchAction`). It is fixed when the step opens, and the payload comes from it alone. */
  kind: StepKind;
  userId: string;
}

/** A PATCH action's button text. The same words are the confirm step's button. */
export const ACTION_LABEL: Record<PatchAction, string> = {
  'make-admin': 'Make admin',
  'remove-admin': 'Remove admin',
  disable: 'Disable',
  enable: 'Enable',
};

const DEFAULT_QUOTA_TEXT: Record<Role, string> = { user: '5', admin: '' };

/** The place of a button in its row. "Disable" and "Enable" share one place, so that focus finds
 * the button again after the change has renamed it. */
const SLOT: Record<StepKind, string> = {
  quota: 'quota',
  reset: 'reset',
  'make-admin': 'role',
  'remove-admin': 'role',
  disable: 'state',
  enable: 'state',
};

/** The opener of a step, found again after the row re-renders, so that focus can go back to it. */
const openerId = (step: Step): string => `${SLOT[step.kind]}:${step.userId}`;

/**
 * `#/admin/users` (docs/specs/accounts.md, sections 4 and 8): list, create, set quota, set role,
 * disable, make a setup link. Patterns: docs/design-system.md, "Admin surfaces".
 */
export function AdminUsersView({ me }: { me: Me }): JSX.Element {
  const { state, inFlight, reload, invalidate, setData } = useLoaded(adminApi.listUsers);
  const [step, setStep] = useState<Step | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<Secret | null>(null);
  /** The opener to focus once the step has closed and its button is back in the row. */
  const focusAfterClose = useRef<string | null>(null);

  useEffect(() => {
    if (step !== null || !focusAfterClose.current) return;
    const id = focusAfterClose.current;
    focusAfterClose.current = null;
    document.querySelector<HTMLElement>(`[data-opener="${id}"]`)?.focus();
  }, [step]);

  function open(next: Step): void {
    setError(null);
    setStep(next);
  }

  function close(): void {
    if (step) focusAfterClose.current = openerId(step);
    setStep(null);
  }

  const users = state.status === 'ready' ? state.data : [];
  const replace = (user: AdminUser): void => setData(users.map((u) => (u.id === user.id ? user : u)));

  /** Run one row action. A refusal leaves the step open, with the server's words above the table. */
  async function act(work: () => Promise<void>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await work();
      close();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const showSecret = (email: string, setupPath: string, reason: Secret['reason']): void =>
    setSecret({ email, reason, url: setupUrl(window.location.origin, setupPath) });

  const meta =
    state.status === 'ready'
      ? [
          `${users.length} users`,
          `${users.filter((u) => u.role === 'admin').length} admins`,
          `${users.filter((u) => userState(u) === 'awaiting setup').length} awaiting setup`,
        ]
      : [];

  return (
    <AdminPage title="Users" meta={meta}>
      {secret && <SecretNotice secret={secret} onDismiss={() => setSecret(null)} />}

      <section className="admin-section">
        <div className="tick">
          <span className="label">§ Everyone</span>
        </div>
        {error && (
          <p className="notice err" role="alert">
            {error}
          </p>
        )}
        {state.status !== 'ready' ? (
          <LoadState state={state} inFlight={inFlight} onRetry={reload} />
        ) : (
          <div className="panel scroll-x">
            <table className="data-table">
              <caption className="sr-only">Users</caption>
              <thead>
                <tr>
                  {['email', 'role', 'runs used', 'state', 'actions'].map((heading) => (
                    <th key={heading} scope="col">
                      <span className="label">{heading}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={user.email === me.email}
                    step={step?.userId === user.id ? step : null}
                    locked={busy || step !== null}
                    busy={busy}
                    onOpen={open}
                    onCancel={close}
                    onSaveQuota={(quota) =>
                      act(async () => replace(await adminApi.updateUser(user.id, { monthly_run_quota: quota })))
                    }
                    onConfirm={(confirmed) =>
                      act(async () => {
                        if (confirmed.kind === 'reset') {
                          const made = await adminApi.makeSetupLink(user.id);
                          replace(made.user);
                          showSecret(made.user.email, made.setup_path, 'reset');
                        } else if (confirmed.kind !== 'quota') {
                          // The payload comes from the button that was pressed, never from this
                          // page's copy of the user (derive.ts, `PatchAction`). The row then
                          // takes the server's user, which is the truth after the change.
                          replace(await adminApi.updateUser(user.id, patchFor(confirmed.kind)));
                        }
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="admin-section">
        <div className="tick">
          <span className="label">§ New user</span>
        </div>
        <CreateUserForm
          onCreated={(made) => {
            // Without the list on screen, a list of one would be a false statement about who
            // exists (derive.ts, `afterCreate`). The setup link shows in both cases: it exists
            // once, and a failed list is no reason to lose it.
            const next = afterCreate(state, made.user);
            if (next.kind === 'append') setData(next.users);
            else invalidate();
            showSecret(made.user.email, made.setup_path, 'created');
          }}
        />
      </section>
    </AdminPage>
  );
}

function UserRow({
  user,
  isSelf,
  step,
  locked,
  busy,
  onOpen,
  onCancel,
  onSaveQuota,
  onConfirm,
}: {
  user: AdminUser;
  isSelf: boolean;
  /** This row's open step, or null. */
  step: Step | null;
  /** Some row is in a step, or a request is in flight: no other step may open. */
  locked: boolean;
  busy: boolean;
  onOpen: (step: Step) => void;
  onCancel: () => void;
  onSaveQuota: (quota: number | null) => Promise<void>;
  onConfirm: (step: Step) => Promise<void>;
}): JSX.Element {
  const current = userState(user);
  const note = userStateNote(user);
  const confirming = step && step.kind !== 'quota' ? step : null;

  const opener = (kind: Step['kind'], text: string): JSX.Element => (
    <button
      className="btn sm"
      type="button"
      disabled={locked}
      data-opener={openerId({ kind, userId: user.id })}
      aria-label={`${text}: ${user.email}`}
      onClick={() => onOpen({ kind, userId: user.id })}
    >
      {text}
    </button>
  );

  return (
    <tr>
      <th scope="row" className="mono">
        {user.email}
        {isSelf && <span className="chip admin-you">you</span>}
      </th>
      <td>{user.role}</td>
      <td className="mono">
        {step?.kind === 'quota' ? (
          <QuotaEdit user={user} busy={busy} onSave={onSaveQuota} onCancel={onCancel} />
        ) : (
          <span className={user.monthly_run_quota == null ? 'muted' : undefined}>{quotaCell(user)}</span>
        )}
      </td>
      <td>
        <span className={`key ${USER_STATE_KEY[current]}`}>{current}</span>
        {note && <span className="admin-state-note">{note}</span>}
      </td>
      <td>
        {confirming ? (
          <ConfirmStep user={user} step={confirming} busy={busy} onConfirm={() => onConfirm(confirming)} onCancel={onCancel} />
        ) : (
          <div className="row-actions">
            {opener('quota', 'Edit quota')}
            {/* An admin's own row offers nothing the server would refuse (design-system.md, "Row
                actions"): no role change, no disable, no reset. The account page changes a password. */}
            {!isSelf && opener(roleAction(user), ACTION_LABEL[roleAction(user)])}
            {!isSelf && !user.disabled && opener('reset', user.has_password ? 'Reset password' : 'New setup link')}
            {!isSelf && opener(stateAction(user), ACTION_LABEL[stateAction(user)])}
          </div>
        )}
      </td>
    </tr>
  );
}

/** Inline edit of one quota (docs/design-system.md, "Inline edit"). Empty means no limit. */
function QuotaEdit({
  user,
  busy,
  onSave,
  onCancel,
}: {
  user: AdminUser;
  busy: boolean;
  onSave: (quota: number | null) => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const [text, setText] = useState(user.monthly_run_quota == null ? '' : String(user.monthly_run_quota));
  const [fault, setFault] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = `quota-${user.id}`;

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    const parsed = parseQuotaInput(text);
    if ('fault' in parsed) {
      setFault(parsed.fault);
      inputRef.current?.focus();
      return;
    }
    setFault(null);
    void onSave(parsed.quota);
  }

  return (
    <form className="inline-edit" noValidate onSubmit={onSubmit} onKeyDown={(e) => cancelOnEscape(e, onCancel)}>
      <label className="sr-only" htmlFor={inputId}>
        Runs per month for {user.email}. Leave empty for no limit.
      </label>
      <input
        ref={inputRef}
        id={inputId}
        className="num"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="no limit"
        value={text}
        aria-invalid={fault ? true : undefined}
        aria-describedby={`${inputId}-note`}
        onChange={(e) => setText(e.target.value)}
      />
      <button className="btn sm" type="submit" disabled={busy}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      <button className="btn sm" type="button" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
      <span id={`${inputId}-note`} className={fault ? 'field-error' : 'field-hint'} role={fault ? 'alert' : undefined}>
        {fault ?? 'empty = no limit'}
      </span>
    </form>
  );
}

function cancelOnEscape(event: KeyboardEvent<HTMLElement>, onCancel: () => void): void {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  onCancel();
}

/**
 * What a confirm step says and what its button is called. One sentence: the consequence. For a
 * PATCH action the words follow the action, as the payload does, so the sentence that is confirmed
 * is always the change that is sent.
 */
export function confirmCopy(user: Pick<AdminUser, 'email' | 'has_password'>, kind: StepKind): {
  sentence: string;
  action: string;
} {
  switch (kind) {
    case 'reset':
      return user.has_password
        ? {
            sentence: `Reset the password of ${user.email}? The current password stops working, and every login of this user ends.`,
            action: 'Reset password',
          }
        : {
            sentence: `Make a new setup link for ${user.email}? Any earlier link stops working.`,
            action: 'Make link',
          };
    case 'remove-admin':
      return {
        sentence: `Remove the admin role from ${user.email}? The quota becomes 5 runs per month.`,
        action: ACTION_LABEL[kind],
      };
    case 'make-admin':
      return {
        sentence: `Make ${user.email} an admin? An admin reads every run, manages users, and has no run limit.`,
        action: ACTION_LABEL[kind],
      };
    case 'enable':
      return { sentence: `Enable ${user.email}? The user can log in again.`, action: ACTION_LABEL[kind] };
    case 'disable':
      return {
        sentence: `Disable ${user.email}? Every login of this user ends, and the user cannot log in.`,
        action: ACTION_LABEL[kind],
      };
    case 'quota':
      return { sentence: '', action: 'Save' };
  }
}

/** The inline confirm step (docs/design-system.md, "Confirm step"). Cancel takes focus first. */
function ConfirmStep({
  user,
  step,
  busy,
  onConfirm,
  onCancel,
}: {
  user: AdminUser;
  step: Step;
  busy: boolean;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const copy = confirmCopy(user, step.kind);
  // Enabling locks nobody out, so its button is plain. Every other confirmation is a danger button.
  const gentle = step.kind === 'enable';

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  return (
    <div className="confirm-step" role="group" aria-label={`Confirm: ${copy.action}`} onKeyDown={(e) => cancelOnEscape(e, onCancel)}>
      <p>{copy.sentence}</p>
      <button className={gentle ? 'btn sm' : 'btn sm danger'} type="button" disabled={busy} onClick={() => void onConfirm()}>
        {busy ? 'Working…' : copy.action}
      </button>
      <button ref={cancelRef} className="btn sm" type="button" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/** The copy-once setup link (docs/design-system.md, "Secret notice"). */
export function SecretNotice({ secret, onDismiss }: { secret: Secret; onDismiss: () => void }): JSX.Element {
  const [copied, setCopied] = useState<'no' | 'yes' | 'failed'>('no');

  // A second secret replaces the first, and its copy state starts again.
  useEffect(() => setCopied('no'), [secret.url]);

  async function onCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(secret.url);
      setCopied('yes');
    } catch {
      setCopied('failed');
    }
  }

  return (
    <div className="notice secret" role="status">
      <p>
        {secret.reason === 'created' ? 'User created. ' : 'Password reset. '}
        Send this setup link to <span className="mono">{secret.email}</span>. It works once, and for {SETUP_LINK_HOURS}{' '}
        hours. It is shown only now.
      </p>
      <code className="secret-value">{secret.url}</code>
      <div className="secret-actions">
        <button className="btn sm" type="button" onClick={() => void onCopy()}>
          Copy link
        </button>
        <button className="btn sm" type="button" onClick={onDismiss}>
          Dismiss
        </button>
        <span aria-live="polite">
          {copied === 'yes' && 'Copied.'}
          {copied === 'failed' && 'Copy failed — select the link and copy it by hand.'}
        </span>
      </div>
    </div>
  );
}

function CreateUserForm({ onCreated }: { onCreated: (made: adminApi.UserWithSetupPath) => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [quotaText, setQuotaText] = useState(DEFAULT_QUOTA_TEXT.user);
  const [quotaTouched, setQuotaTouched] = useState(false);
  const [emailFault, setEmailFault] = useState<string | null>(null);
  const [quotaFault, setQuotaFault] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function onRole(next: Role): void {
    setRole(next);
    // The quota follows the role's default until somebody types one.
    if (!quotaTouched) setQuotaText(DEFAULT_QUOTA_TEXT[next]);
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setError(null);
    setEmailFault(null);
    setQuotaFault(null);
    if (!email.trim()) {
      setEmailFault('Enter an email address.');
      document.getElementById('new-user-email')?.focus();
      return;
    }
    const parsed = parseQuotaInput(quotaText);
    if ('fault' in parsed) {
      setQuotaFault(parsed.fault);
      document.getElementById('new-user-quota')?.focus();
      return;
    }
    setBusy(true);
    try {
      onCreated(await adminApi.createUser({ email: email.trim(), role, monthly_run_quota: parsed.quota }));
      setEmail('');
      setRole('user');
      setQuotaText(DEFAULT_QUOTA_TEXT.user);
      setQuotaTouched(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form admin-create" noValidate onSubmit={(e) => void onSubmit(e)}>
      <Field
        id="new-user-email"
        label="Email"
        type="email"
        autoComplete="off"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={emailFault}
      />
      <div className="field">
        <label htmlFor="new-user-role">Role</label>
        <select id="new-user-role" value={role} onChange={(e) => onRole(e.target.value === 'admin' ? 'admin' : 'user')}>
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
      </div>
      <Field
        id="new-user-quota"
        label="Runs per month"
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={quotaText}
        onChange={(e) => {
          setQuotaText(e.target.value);
          setQuotaTouched(true);
        }}
        hint="Leave empty for no limit."
        error={quotaFault}
      />
      {error && (
        <p className="notice err" role="alert">
          {error}
        </p>
      )}
      <button className="btn primary" type="submit" disabled={busy}>
        {busy ? 'Creating…' : 'Create user'}
      </button>
    </form>
  );
}
